import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { ReactComponent as InfoIcon } from '../../../../../../assets/icons/info-color-icon-ne.svg';
import BdsCircleIndicatorPictogram from '../../../../../../shared/components/v3/BdsCircleIndicator/BdsCircleIndicatorPictogram/BdsCircleIndicatorPictogram';
import { HEALTH_INSURANCE_ICON } from '../../../../../../shared/constants/images';
import { FLEXIBLE_REMUNERATION_REQUEST_STATUS } from '../../../../../../shared/constants/requests';
import { CompanyContext } from '../../../../../../shared/context/company/companyContext';
import { IMembersData } from '../../../../../../shared/interfaces/businessData';
import { IFlexibleRemunerationRequest } from '../../../../../../shared/interfaces/company';
import Companies from '../../../../../../shared/services/api/collaborators/collaborators';
import { TRANSLATIONS_COLLABORATOR_GENDER } from '../../../../../../shared/translations/collaborators';
import { TRANSLATIONS_REQUESTS } from '../../../../../../shared/translations/requests';

import './InsuranceRequestDetails.scss';

interface IInsuranceRequestDetailsProps {
  request: IFlexibleRemunerationRequest;
}

const InsuranceRequestDetails = ({ request }: IInsuranceRequestDetailsProps) => {
  const [dynamoMember, setDynamoMember] = useState<IMembersData | undefined>(undefined);
  const [loadingDynamoMember, setLoadingDynamoMember] = useState<boolean>(true);
  const { company } = useContext(CompanyContext);
  const { t } = useTranslation();

  const memberData = request.member;

  const phone = memberData?.phone ?? '';
  const street = memberData?.address?.street ?? '';
  const city = memberData?.address?.city ?? '';

  const hasPhone = Boolean(phone);
  const hasAddress = Boolean(street || city);
  const hasPhoneOrAddress = hasPhone || hasAddress;

  const status = request.status?.toLowerCase();
  const isPending = status === FLEXIBLE_REMUNERATION_REQUEST_STATUS.pending.value;

  const getMemberFromDynamo = useCallback(async () => {
    setLoadingDynamoMember(true);
    try {
      const playerId = request.member.playerId;
      if (!playerId) return;

      const response = await Companies.getFilteredMembers(company.id, undefined, undefined, playerId);
      setDynamoMember(response.members[0]);
    } catch {
      setDynamoMember(undefined);
    } finally {
      setLoadingDynamoMember(false);
    }
  }, [company.id, request.member.playerId]);

  useEffect(() => {
    getMemberFromDynamo();
  }, [getMemberFromDynamo]);

  const aside = useMemo(
    () => (
      <aside className="ird-sidebar flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <BdsCircleIndicatorPictogram pictogram={{ component: HEALTH_INSURANCE_ICON }} circleIndicatorSize="l" />
          <p className="ird-sidebar-title">{t(TRANSLATIONS_REQUESTS.details.insurance.healthInsuranceLabel)}</p>
        </div>

        <div className="flex flex-col gap-8">
          <div className="ird-sidebar-item">
            <p className="label-sidebar">{t(TRANSLATIONS_REQUESTS.details.insurance.companyNameLabel)}</p>
            <p className="value-sidebar">{request.details.insuranceCompanyName}</p>
          </div>

          <div className="ird-sidebar-item">
            <p className="label-sidebar">{t(TRANSLATIONS_REQUESTS.details.insurance.policyNumberLabel)}</p>
            <p className="value-sidebar">{request.details.policyNumber}</p>
          </div>
        </div>
      </aside>
    ),
    [request.details.insuranceCompanyName, request.details.policyNumber, t]
  );

  const banner = useMemo(() => {
    if (!isPending) return null;
    return (
      <div className="ird-info-banner flex items-center gap-4">
        <InfoIcon className="icon" />
        <p className="value">{t(TRANSLATIONS_REQUESTS.details.insurance.infoBanner)}</p>
      </div>
    );
  }, [isPending, t]);

  if (loadingDynamoMember) {
    return (
      <Skeleton
        variant="rectangular"
        width="100%"
        height={454}
        sx={{ borderRadius: '32px' }}
        data-testid="ird-skeleton"
      />
    );
  }

  const secondaryInDetails = hasPhoneOrAddress ? aside : banner;
  const secondaryOutsideDetails = hasPhoneOrAddress ? banner : aside;

  return (
    <div className="insurance-request-details" data-experience="new">
      <div className="ird-title">{t(TRANSLATIONS_REQUESTS.details.insurance.title)}</div>
      <div className={`flex gap-10 ${hasPhoneOrAddress ? 'flex-col' : 'flex-row'}`}>
        <div className={`ird-details flex gap-10 ${hasPhoneOrAddress ? 'flex-row' : 'flex-col'}`}>
          <div className="ird-grid">
            <div className="ird-grid-item">
              <p className="label">{t(TRANSLATIONS_REQUESTS.details.insurance.collaboratorLabel)}</p>
              <p className="value">{`${memberData?.firstName ?? ''} ${memberData?.lastName ?? ''}`.trim()}</p>
            </div>
            <div className="ird-grid-item">
              <p className="label">{t(TRANSLATIONS_REQUESTS.details.insurance.identificationLabel)}</p>
              <p className="value">{memberData?.nationalId}</p>
            </div>
            <div className="ird-grid-item">
              <p className="label">{t(TRANSLATIONS_REQUESTS.details.insurance.genderLabel)}</p>
              {dynamoMember?.gender && TRANSLATIONS_COLLABORATOR_GENDER[dynamoMember.gender] ? (
                <p className="value">{t(TRANSLATIONS_COLLABORATOR_GENDER[dynamoMember.gender])}</p>
              ) : (
                <p className="value-not-informed">{t(TRANSLATIONS_REQUESTS.details.insurance.notInformed)}</p>
              )}
            </div>
            <div className="ird-grid-item">
              <p className="label">{t(TRANSLATIONS_REQUESTS.details.insurance.birthDateLabel)}</p>
              <p className="value">{memberData?.birthdate}</p>
            </div>
            <div className="ird-grid-item">
              <p className="label">{t(TRANSLATIONS_REQUESTS.details.insurance.emailLabel)}</p>
              <p className="value">{dynamoMember?.email}</p>
            </div>
            {hasPhone && (
              <div className="ird-grid-item">
                <p className="label">{t(TRANSLATIONS_REQUESTS.details.insurance.phoneLabel)}</p>
                <p className="value">{phone}</p>
              </div>
            )}
            {hasAddress && (
              <div className="ird-grid-item">
                <p className="label">{t(TRANSLATIONS_REQUESTS.details.insurance.addressLabel)}</p>
                <p className="value">{`${street} ${city}`.trim()}</p>
              </div>
            )}
          </div>
          {secondaryInDetails}
        </div>
        {secondaryOutsideDetails}
      </div>
    </div>
  );
};

export default InsuranceRequestDetails;
